import { ENDPOINT_NAMES } from '../../../constants';
import type {
  PutProjectsByProjectIdAudienceBuilderAudienceApiArg,
  PutProjectsByProjectIdAudienceBuilderAudienceApiResponse,
} from '../../../rtk/rtk.generated';
import type { ProjectApi } from '../../../rtk/types';
import type { AppDispatch } from '../../types';
import { OptimisticUpdater } from '../../utils';

type UpsertAudienceArgs = Pick<PutProjectsByProjectIdAudienceBuilderAudienceApiArg, 'projectId'>;

interface UpsertAudienceContext {
  dispatch: AppDispatch;
  queryFulfilled: Promise<{ data: PutProjectsByProjectIdAudienceBuilderAudienceApiResponse }>;
}

/**
 * ### Audience upsert cache write
 *
 * @description
 * The PUT answers with the saved document, so the response fills the audience query's cache entry
 * instead of invalidating it — the invalidation refetched one of the larger payloads we fetch, on
 * every debounced autosave, for a response the editors never read (hydration is one-shot, EPD-1326).
 *
 * Unlike its siblings in this folder the write is not optimistic — it lands after the server answers,
 * with server truth, so there is nothing to roll back.
 *
 * @param api - The ProjectApi instance
 * @returns Endpoint enhancer configuration
 */
const putProjectsByProjectIdAudienceBuilderAudience = (api: ProjectApi) => ({
  // Explicitly empty rather than absent: with no override the generated `Audience` tag applies,
  // which refetches audience attachments.
  invalidatesTags: [],
  async onQueryStarted(
    { projectId }: UpsertAudienceArgs,
    { dispatch, queryFulfilled }: UpsertAudienceContext,
  ) {
    try {
      const { data } = await queryFulfilled;

      // `upsertData`, not `update`: the prototype flow saves an audience on a page that never reads
      // one, and `updateQueryData` silently no-ops when there is no entry to patch.
      new OptimisticUpdater({ dispatch, api }).upsertData({
        endpointName: ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_AUDIENCE_BUILDER_AUDIENCE,
        args: { projectId },
        data,
      });
    } catch {
      // A failed save leaves the cache alone. The draft keeps the write pending and retries on the
      // next edit, and `useUpsertAudienceDocument` has already notified; refetching here would
      // refresh a cache entry nobody is waiting on, and could not reach the draft either way.
    }
  },
});

export default putProjectsByProjectIdAudienceBuilderAudience;
